import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  FaUsers, 
  FaMoneyBillWave, 
  FaUser, 
  FaCog, 
  FaUserShield,
  FaBuilding,
  FaChartLine,
  FaWallet
} from "react-icons/fa";
import axios from "axios";
import { getAdmins, getOrganizations, fetchOrgUsers } from "../../services/api";
import "../../../styles/dashboard/SuperAdmin/SuperAdminHome.css";

const API_URL = process.env.REACT_APP_API_URL;
const getToken = () => sessionStorage.getItem("token") || localStorage.getItem("token");

/**
 * Super-Admin landing page with statistics and quick-access cards
 */
export default function SuperAdminHome() {
  const [stats, setStats] = useState({
    admins: 0,
    subAdmins: 0,
    organizations: 0,
    users: 0,
    loading: true
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [adminsRes, subAdminsRes, orgsRes, usersRes] = await Promise.all([
        getAdmins(),
        axios.get(`${API_URL}/superadmin/sub-admin`, {
          headers: { Authorization: `Bearer ${getToken()}` }
        }),
        getOrganizations(),
        fetchOrgUsers()
      ]);

      // Extract data - handle different response structures
      let adminsData = adminsRes.success ? adminsRes.data : [];
      let subAdminsData = subAdminsRes.data || [];
      let orgsData = orgsRes.success ? orgsRes.data : [];
      let usersData = usersRes.success ? usersRes.data : [];

      // Handle if data is nested (e.g., {admins: [...]})
      if (adminsData && typeof adminsData === 'object' && !Array.isArray(adminsData)) {
        adminsData = adminsData.admins || adminsData.data || [];
      }
      if (subAdminsData && typeof subAdminsData === 'object' && !Array.isArray(subAdminsData)) {
        subAdminsData = subAdminsData.subAdmins || subAdminsData.data || [];
      }
      if (orgsData && typeof orgsData === 'object' && !Array.isArray(orgsData)) {
        orgsData = orgsData.organizations || orgsData.data || [];
      }
      if (usersData && typeof usersData === 'object' && !Array.isArray(usersData)) {
        usersData = usersData.users || usersData.data || [];
      }

      // Count admins and sub-admins
      const adminsList = Array.isArray(adminsData) ? adminsData : [];
      const subAdminsList = Array.isArray(subAdminsData) ? subAdminsData : [];
      
      const adminsCount = adminsList.filter(admin => {
        const role = admin.role?.toLowerCase().trim();
        return role === 'admin';
      }).length;
      
      const subAdminsCount = subAdminsList.length;

      console.log('Stats data:', { 
        adminsCount, 
        subAdminsCount, 
        orgs: orgsData.length, 
        users: usersData.length 
      });

      setStats({
        admins: adminsCount,
        subAdmins: subAdminsCount,
        organizations: Array.isArray(orgsData) ? orgsData.length : 0,
        users: Array.isArray(usersData) ? usersData.length : 0,
        loading: false
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
      setStats(prev => ({ ...prev, loading: false }));
    }
  };

  const statCards = [
    {
      title: "Total Admins",
      count: stats.admins,
      icon: FaUserShield,
      gradient: "stat-admin",
      link: "/super-admin/admins"
    },
    {
      title: "Coordinate Agents",
      count: stats.subAdmins,
      icon: FaUsers,
      gradient: "stat-subadmin",
      link: "/super-admin/sub-admins"
    },
    {
      title: "Organizations",
      count: stats.organizations,
      icon: FaBuilding,
      gradient: "stat-org",
      link: "/super-admin/organizations"
    },
    {
      title: "Total Users",
      count: stats.users,
      icon: FaUser,
      gradient: "stat-user",
      link: "/super-admin/users"
    }
  ];
  
  const quickActions = [
    {
      title: "Admins",
      description: "Manage admin accounts and permissions",
      link: "/super-admin/admins",
      Icon: FaUserShield,
      gradient: "action-admin",
    },
    {
      title: "Organizations",
      description: "Manage organizations and agreements",
      link: "/super-admin/organizations",
      Icon: FaBuilding,
      gradient: "action-org",
    },
    {
      title: "Users",
      description: "Monitor and manage user accounts",
      link: "/super-admin/users",
      Icon: FaUser,
      gradient: "action-user",
    },
    {
      title: "Transactions",
      description: "View payment and transaction history",
      link: "/super-admin/transaction-history",
      Icon: FaMoneyBillWave,
      gradient: "action-transaction",
    },
    {
      title: "Credit Reports",
      description: "Manage credit report settings",
      link: "/super-admin/credit-reports",
      Icon: FaChartLine,
      gradient: "action-credit",
    }
  ];

  return (
    <div className="sa-home">
      <header className="sa-header">
        <div className="sa-header-content">
          <h1 className="sa-header-title">Dashboard Overview</h1>
          <p className="sa-header-description">
            Monitor and manage your entire system from this centralized dashboard.
            Track admins, organizations, users, and transactions in real-time.
          </p>
        </div>
      </header>

      {/* Statistics Section */}
      <section className="sa-stats-section">
        <div className="sa-stats-grid">
          {statCards.map((stat, idx) => {
            const IconComponent = stat.icon;
            return (
              <Link key={idx} to={stat.link} className="sa-stat-card-link">
                <div className={`sa-stat-card ${stat.gradient}`}>
                  <div className="sa-stat-icon-wrapper">
                    <IconComponent className="sa-stat-icon" />
                  </div>
                  <div className="sa-stat-content">
                    <h3 className="sa-stat-title">{stat.title}</h3>
                    <p className="sa-stat-count">
                      {stats.loading ? (
                        <span className="sa-stat-loading">...</span>
                      ) : (
                        stat.count
                      )}
                    </p>
                  </div>
                  <div className="sa-stat-overlay"></div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Quick Actions Section */}
      <section className="sa-quick-actions-section">
        <h2 className="sa-section-title">Quick Actions</h2>
        <div className="sa-quick-actions-grid">
          {quickActions.map((action, idx) => {
            const IconComponent = action.Icon;
            return (
              <Link key={idx} to={action.link} className="sa-action-card-link">
                <div className={`sa-action-card ${action.gradient}`}>
                  <div className="sa-action-icon-wrapper">
                    <IconComponent className="sa-action-icon" />
                  </div>
                  <div className="sa-action-content">
                    <h3 className="sa-action-title">{action.title}</h3>
                    <p className="sa-action-description">{action.description}</p>
                  </div>
                  <div className="sa-action-arrow">→</div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
}

